import { useEffect, useRef, useState } from "react";
import { Star, PenLine, Quote, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";
import ReviewModal from "@/components/ReviewModal";

const T = {
  es: { title: "RESEÑAS", write: "Escribir reseña", empty: "¡Sé el primero!" },
  en: { title: "REVIEWS", write: "Write a review", empty: "Be the first!" },
};

const Stars = ({ n, size = 12 }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} strokeWidth={2} className={i <= n ? "text-[#FFC107]" : "text-white/20"} fill={i <= n ? "#FFC107" : "none"} />
    ))}
  </span>
);

// One auto-scrolling vertical column of review cards (marquee, pause on hover).
const Column = ({ items, isAdmin, onDelete, onOpen, side, testid }) => {
  const ref = useRef(null);
  const paused = useRef(false);
  const posRef = useRef(0);
  const auto = items.length >= 3;
  useEffect(() => {
    if (!auto) return;
    const el = ref.current;
    if (!el) return;
    posRef.current = el.scrollTop;
    const id = setInterval(() => {
      if (paused.current) return;
      const half = el.scrollHeight / 2;
      if (half <= 0) return;
      // Accumulate in a float and assign — mobile browsers round scrollTop to
      // integers, so reading it back (`+= 0.4`) would floor to 0 and never move.
      posRef.current += 0.4;
      if (posRef.current >= half) posRef.current -= half;
      el.scrollTop = posRef.current;
    }, 30);
    return () => clearInterval(id);
  }, [auto, items.length]);

  const rendered = auto ? [...items, ...items] : items;
  return (
    <div
      ref={ref}
      data-testid={testid}
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
      className="flex-1 min-h-0 overflow-y-auto review-scroll flex flex-col gap-1.5 sm:gap-2.5 pr-0.5 sm:pr-1"
    >
      {rendered.map((r, i) => (
        <div
          key={`${r.id}-${i}`}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(r)}
          onKeyDown={(e) => { if (e.key === "Enter") onOpen(r); }}
          className={`relative pixel-card bg-[#141414]/90 p-2 sm:p-3 lg:p-4 cursor-pointer hover:bg-[#1f1f1f]/90 transition-colors ${side === "right" ? "text-right" : ""}`}
          data-testid="review-card"
        >
          <div className={`flex items-center gap-1 sm:gap-1.5 ${side === "right" ? "justify-end" : ""}`}>
            <Quote size={11} className="text-white/25 hidden sm:block" /><Stars n={r.rating} />
          </div>
          {r.text && <p className="font-vt text-[11px] sm:text-base lg:text-lg text-white/85 leading-tight mt-1 line-clamp-3">{r.text}</p>}
          <p className="font-vt text-[10px] sm:text-sm lg:text-base text-[#FFEB3B] mt-1">— {r.name}</p>
          {isAdmin && (
            <button data-testid={`corner-review-delete-${r.id}`} onClick={(e) => { e.stopPropagation(); onDelete(r.id); }} title="Delete"
              className="absolute top-1.5 right-1.5 text-white/40 hover:text-[#ff5252]"><Trash2 size={12} /></button>
          )}
        </div>
      ))}
    </div>
  );
};

// Popup that shows a single review in full when its card is clicked.
const ReviewViewModal = ({ review, onClose }) => (
  <div
    data-testid="review-view-modal"
    onClick={onClose}
    className="fixed inset-0 z-[95] overflow-y-auto overscroll-contain flex items-start justify-center bg-black/85 p-4 scanlines"
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="relative w-full max-w-md pixel-card bg-[#141414] p-6 max-h-full overflow-y-auto my-auto"
    >
      <button data-testid="review-view-close" onClick={onClose}
        className="absolute top-3 right-3 text-white/50 hover:text-white"><X size={20} /></button>
      <div className="flex items-center gap-2">
        <Quote size={20} className="text-white/25" /><Stars n={review.rating} size={18} />
      </div>
      {review.text
        ? <p className="font-vt text-xl text-white/90 leading-snug mt-4 whitespace-pre-wrap">{review.text}</p>
        : <p className="font-vt text-lg text-white/40 italic mt-4">★★★★★</p>}
      <p className="font-vt text-lg text-[#FFEB3B] mt-4">— {review.name}</p>
    </div>
  </div>
);

export const ReviewsCorners = () => {
  const { lang } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const t = T[lang] || T.es;
  const [data, setData] = useState({ items: [], avg: 0, count: 0 });
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = () => api.listReviews(20).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== id) }));
    try { await api.deleteReview(id); toast.success("Deleted"); } catch (e) { toast.error("Error"); load(); }
  };

  // alternate items into left / right columns
  const left = data.items.filter((_, i) => i % 2 === 0);
  const right = data.items.filter((_, i) => i % 2 === 1);

  const WriteBtn = () => (
    <button data-testid="write-review-btn" onClick={() => setOpen(true)}
      className="pixel-btn bg-[#FFB300] text-black px-2 sm:px-3 py-1 sm:py-1.5 text-[8px] sm:text-[10px] lg:text-xs flex items-center gap-1 shrink-0">
      <PenLine size={11} strokeWidth={2.5} /> {t.write}
    </button>
  );

  return (
    <>
      {/* Titled rail — moved to the RIGHT side */}
      <div className="flex absolute right-1 sm:right-2 top-14 sm:top-16 bottom-11 sm:bottom-12 z-20 w-[5rem] sm:w-40 lg:w-72 flex-col gap-1.5 sm:gap-2.5 items-end" data-testid="reviews-corner-left">
        <div className="flex items-center justify-between gap-1.5">
          <div className="flex items-center gap-1 sm:gap-1.5 min-w-0">
            <h3 className="font-pixel text-[7px] sm:text-[11px] lg:text-sm text-[#FFEB3B] pixel-stroke truncate">{t.title}</h3>
            {data.count > 0 && <span className="font-vt text-[11px] sm:text-base lg:text-lg text-white/70 whitespace-nowrap" data-testid="reviews-corner-avg">{data.avg}★·{data.count}</span>}
          </div>
        </div>
        <WriteBtn />
        {data.items.length === 0
          ? <p className="font-vt text-[11px] sm:text-base text-white/45">{t.empty}</p>
          : <Column items={left} isAdmin={isAdmin} onDelete={del} onOpen={setViewing} side="right" testid="reviews-list-left" />}
      </div>

      {/* Plain rail — moved to the LEFT side */}
      <div className="flex absolute left-1 sm:left-2 top-14 sm:top-16 bottom-11 sm:bottom-12 z-20 w-[5rem] sm:w-40 lg:w-72 flex-col gap-1.5 sm:gap-2.5" data-testid="reviews-corner-right">
        <WriteBtn />
        {right.length > 0
          ? <Column items={right} isAdmin={isAdmin} onDelete={del} onOpen={setViewing} side="left" testid="reviews-list-right" />
          : <div className="flex-1" />}
      </div>

      {viewing && <ReviewViewModal review={viewing} onClose={() => setViewing(null)} />}
      {open && <ReviewModal onClose={() => setOpen(false)} onSubmitted={load} />}
    </>
  );
};
